import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject} from 'rxjs';
import {environment} from '../../environments/environment';
import {SearchResult} from '../_models/_Index';
import {
  AiChatAmbitoContesto, AiChatContesti, AiChatContesto, AiChatContestoProposta, AiChatContestoVersione,
  AiChatSalvaContesto
} from '../_models/ai-chat.model';
import {richiestaAiChat} from './ai-chat.service';

/** Feature 164: API dei contesti della chat (AgentApi, gateway `agent/`) e proposta dell'assistente in attesa
 * di essere aperta nell'editor (research R8). */
@Injectable({providedIn: 'root'})
export class AiChatContestiService {
  private readonly baseUrl = `${environment.localApiUrl}agent/ai-chat/contesti`;
  private readonly http = inject(HttpClient);
  private readonly propostaSubject = new BehaviorSubject<AiChatContestoProposta | null>(null);
  readonly proposta$ = this.propostaSubject.asObservable();

  getAsync(orgId: number): Promise<AiChatContesti> {
    return richiestaAiChat(this.http.get<AiChatContesti>(`${this.baseUrl}/${orgId}`));
  }

  salvaAsync(orgId: number, ambito: AiChatAmbitoContesto, request: AiChatSalvaContesto): Promise<AiChatContesto> {
    return richiestaAiChat(this.http.put<AiChatContesto>(`${this.baseUrl}/${orgId}/${ambito}`, request));
  }

  versioniAsync(orgId: number, ambito: AiChatAmbitoContesto, page = 0, count = 20):
    Promise<SearchResult<AiChatContestoVersione>> {
    return richiestaAiChat(this.http.post<SearchResult<AiChatContestoVersione>>(`${this.baseUrl}/${orgId}/versioni/cerca`,
      {ambito, page, count}));
  }

  versioneAsync(orgId: number, ambito: AiChatAmbitoContesto, versione: number): Promise<AiChatContestoVersione> {
    return richiestaAiChat(this.http.get<AiChatContestoVersione>(`${this.baseUrl}/${orgId}/versioni/${ambito}/${versione}`));
  }

  /** Ripristina una versione o, solo per il generale, il testo predefinito: crea sempre una nuova versione. */
  ripristinaAsync(orgId: number, ambito: AiChatAmbitoContesto, versioneLetta: number,
                  origine: {versione: number} | {predefinito: true}): Promise<AiChatContesto> {
    return richiestaAiChat(this.http.post<AiChatContesto>(`${this.baseUrl}/${orgId}/ripristina`,
      {ambito, versioneLetta, ...origine}));
  }

  /** «Apri nell'editor» della chat: la proposta resta in attesa finché l'editor giusto non la consuma. */
  impostaProposta(proposta: AiChatContestoProposta) {
    this.propostaSubject.next(proposta);
  }

  /** La proposta in attesa se è per questo ambito e organizzazione; consumata, non torna più. */
  consumaProposta(ambito: AiChatAmbitoContesto, orgId: number): AiChatContestoProposta | null {
    const proposta = this.propostaSubject.value;
    if (!proposta || proposta.ambito !== ambito || proposta.organizzazioneId !== Number(orgId)) return null;
    this.propostaSubject.next(null);
    return proposta;
  }
}
